import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useDialog } from '@/lib/hooks/use-dialog'
import { deleteCaseAlertMutation, upsertCaseAlertMutation } from '@/lib/queries/business/v1/alert/mutations'
import { getCaseAlertListQueryKey } from '@/lib/queries/business/v1/alert/queries'
import { type CaseAlertTimeUnit, type ReqUpsertCaseAlertMutation } from '@/lib/queries/business/v1/alert/types'
import { getUtcDateString } from '@/lib/utils/datetime'

import { useAlertDialog } from '../../hooks/use-alert-dialog'
import { type CaseAlertFormSchema, type CaseAlertFormSchemaRequired } from '../../types'
import { type EditAlertCardProps } from './edit-alert-card'

type UseEditAlertCardParams = Pick<EditAlertCardProps, 'alertData' | 'closeDialog'>

export const useEditAlertCard = ({ alertData, closeDialog }: UseEditAlertCardParams) => {
  const { form, formErrors, values, markAlertTypeError, markAlertDateError, markCountError, markUnitError } =
    useAlertDialog({
      defaultValues: alertData,
    })

  const { mutateAsync: updateCaseAlert, isPending: isPendingUpdateCaseAlert } = useMutation(upsertCaseAlertMutation)
  const { mutateAsync: deleteCaseAlert, isPending: isPendingDeleteCaseAlert } = useMutation(deleteCaseAlertMutation)

  const {
    isDialogOpen: isDeleteConfirmDialogOpen,
    openDialog: openDeleteConfirmDialog,
    closeDialog: closeDeleteConfirmDialog,
  } = useDialog()

  const isValidData = (data: CaseAlertFormSchema): data is CaseAlertFormSchemaRequired => {
    const { alertType, alertDate, notificationSettings } = data

    return Boolean(alertType && alertDate && notificationSettings?.count && notificationSettings?.unit)
  }

  const handleClickSaveButton = async () => {
    if (!isValidData(values)) {
      return
    }

    const payload: ReqUpsertCaseAlertMutation = [
      {
        id: alertData.id,
        caseId: alertData.caseId,
        alertType: values.alertType,
        deadline: getUtcDateString(values.alertDate),
        timeAgo: Number(values.notificationSettings?.count),
        timeUnit: values.notificationSettings?.unit as CaseAlertTimeUnit,
      },
    ]

    await updateCaseAlert(payload, {
      onSuccess: () => {
        toast.success('Alert updated successfully.')
        closeDialog()
        setTimeout(async () => {
          await queryClient.invalidateQueries({
            queryKey: getCaseAlertListQueryKey(alertData.caseId),
          })
        }, 2000)
      },
      onError: () => {
        toast.error('Failed to update alert.')
      },
    })
  }

  const handleClickDeleteButton = async () => {
    await deleteCaseAlert(
      {
        ids: [alertData.id],
      },
      {
        onSuccess: () => {
          toast.success('Alert deleted successfully.')
          closeDialog()
          setTimeout(() => {
            void queryClient.invalidateQueries({
              queryKey: getCaseAlertListQueryKey(alertData.caseId),
            })
          }, 2000)
        },
        onError: () => {
          toast.error('Failed to delete alert.')
        },
      }
    )
  }

  const hasFormErrors = Object.keys(formErrors).length > 0
  const hasErrors = hasFormErrors || !isValidData(values)

  /**
   * UI
   */
  const isDisabledSaveButton = hasErrors || isPendingDeleteCaseAlert
  const isDisabledDeleteButton = isPendingUpdateCaseAlert
  const isLoadingSaveButton = isPendingUpdateCaseAlert
  const isLoadingDeleteButton = isPendingDeleteCaseAlert

  return {
    form,
    formErrors,
    isDisabledSaveButton,
    isLoadingSaveButton,
    isDisabledDeleteButton,
    isLoadingDeleteButton,
    isDeleteConfirmDialogOpen,
    markAlertTypeError,
    markAlertDateError,
    markCountError,
    markUnitError,
    openDeleteConfirmDialog,
    closeDeleteConfirmDialog,
    handleClickSaveButton,
    handleClickDeleteButton,
  }
}
